/**
 * TenantThemeContext — fetches the active tenant's UI theme on mount and
 * applies every token as a `--vl-*` CSS variable on :root, so any element
 * styled with `var(--vl-btn-primary-from)` etc. instantly inherits the
 * per-tenant look. Also injects `theme.custom_css` verbatim into <head>.
 *
 * Resolution order:
 *   1. `X-Tenant-Slug` derived from the URL (e.g. /groupa/app → "groupa")
 *   2. Tenant default chrome while the backend theme is loading
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import api from '../services/api';
import { tenantSlugFromPathOrHost } from '../utils/tenantDomains';

// The branded boot screen is the player's brand moment. Staff surfaces —
// admin, the agent portal, the Agents Hub — have their own look, so showing
// them a Slots Oceans logo over an ocean background on every refresh is just
// a flash of the wrong product.
const STAFF_SURFACE = /(^|\/)(admin|staff-portal|agent-portal|agent|agents-hub|finance|portal)(\/|$)/i;
const isStaffSurface = () => STAFF_SURFACE.test(window.location.pathname || '');

const TenantThemeContext = createContext(null);

const DEFAULT_TENANT_CHROME = {
  bg: 'linear-gradient(90deg, #152235 0%, #17283A 34%, #1D3349 67%, #223B54 100%)',
  landing_bg: 'linear-gradient(90deg, #152235 0%, #17283A 34%, #1D3349 67%, #223B54 100%)',
  surface: 'rgba(21, 34, 53, 0.92)',
  surface_alt: 'rgba(29, 51, 73, 0.88)',
  header_from: '#152235',
  header_to: '#223B54',
};

const withTenantOverrides = (slug, theme) => {
  const base = theme || {};
  if (!slug) return base;
  return { ...DEFAULT_TENANT_CHROME, ...base };
};

const tenantThemeCacheKey = (slug) => `vl_tenant_theme:${String(slug || '').toLowerCase()}`;
const installIconHrefForSlug = (slug) => `/api/tenants/public/${encodeURIComponent(slug)}/install-icon`;
const MIN_PLAYER_REFRESH_MS = 900;

const isPlayerAppPath = () => {
  if (typeof window === 'undefined') return false;
  return /^\/(?:slotsoceans\/)?app(?:\/|$)/i.test(window.location.pathname || '');
};

const waitForPlayerRefreshWindow = async () => {
  if (!isPlayerAppPath() || typeof performance === 'undefined') return;
  const remaining = MIN_PLAYER_REFRESH_MS - performance.now();
  if (remaining > 0) {
    await new Promise((resolve) => window.setTimeout(resolve, remaining));
  }
};

const readCachedTheme = (slug) => {
  if (!slug) return null;
  try {
    const raw = localStorage.getItem(tenantThemeCacheKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedTheme = (slug, theme) => {
  if (!slug || !theme || typeof theme !== 'object') return;
  try {
    localStorage.setItem(tenantThemeCacheKey(slug), JSON.stringify(theme));
  } catch {
    // Ignore storage failures; live theme still applies for this page.
  }
};

const getSlugFromPath = () => {
  return tenantSlugFromPathOrHost();
};

// Map a flat theme dict { btn_primary_from: "#22FF88", ... } to CSS
// custom-property pairs { "--vl-btn-primary-from": "#22FF88", ... }.
// All non-string values are skipped so we don't render `[object Object]`.
const themeToCssVars = (theme) => {
  const out = {};
  if (!theme || typeof theme !== 'object') return out;
  for (const [key, val] of Object.entries(theme)) {
    if (typeof val !== 'string' || !val.length) continue;
    if (key === 'custom_css') continue;          // injected separately
    if (key === 'logo_url' || key === 'intro_logo_url' || key === 'brand_name' || key === 'tagline') continue;
    const cssKey = '--vl-' + key.replace(/_/g, '-');
    out[cssKey] = val;
  }
  return out;
};

export function TenantThemeProvider({ children }) {
  const initialSlug = getSlugFromPath();
  const cachedInitialTheme = readCachedTheme(initialSlug);
  const [theme, setTheme] = useState(() => withTenantOverrides(initialSlug, cachedInitialTheme || {}));
  const [loading, setLoading] = useState(Boolean(initialSlug));
  const [ready, setReady] = useState(Boolean(!initialSlug));
  const [tenantSlug, setTenantSlug] = useState(initialSlug);

  const applyTheme = useCallback((t) => {
    if (!t || typeof t !== 'object') return;
    const root = document.documentElement;
    const vars = themeToCssVars(t);
    for (const [k, v] of Object.entries(vars)) {
      root.style.setProperty(k, v);
    }
    // Inject custom CSS verbatim — replace any previous block so admins
    // can iterate without page reload.
    const styleId = 'vl-tenant-theme-custom-css';
    let styleEl = document.getElementById(styleId);
    const cssBody = (t.custom_css || '').toString().trim();
    if (cssBody) {
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = cssBody;
    } else if (styleEl) {
      styleEl.remove();
    }
  }, []);

  const fetchTheme = useCallback(async (slug) => {
    setLoading(true);
    try {
      document.documentElement.dataset.tenantSlug = slug || '';
      const res = await api.get('/api/tenant/theme', {
        headers: slug ? { 'X-Tenant-Slug': slug } : {},
      });
      const t = withTenantOverrides(slug, res.data?.theme || {});
      setTheme(t);
      applyTheme(t);
      writeCachedTheme(slug, t);
    } catch (e) {
      // Fail silent — page just renders with the Tailwind hard-coded look.
      console.warn('[TenantTheme] fetch failed', e?.message);
      const fallback = withTenantOverrides(slug, readCachedTheme(slug) || {});
      setTheme(fallback);
      applyTheme(fallback);
    } finally {
      await waitForPlayerRefreshWindow();
      setLoading(false);
      setReady(true);
    }
  }, [applyTheme]);

  useEffect(() => {
    if (tenantSlug && !readCachedTheme(tenantSlug)) {
      setReady(false);
    }
    fetchTheme(tenantSlug);
  }, [fetchTheme, tenantSlug]);

  // Re-fetch on URL change (single-page navigation between /groupa/app and /app)
  useEffect(() => {
    const handler = () => {
      const next = getSlugFromPath();
      if (next !== tenantSlug) {
        const cached = readCachedTheme(next);
        const nextTheme = withTenantOverrides(next, cached || {});
        setTheme(nextTheme);
        applyTheme(nextTheme);
        // Keep the branded boot surface visible until the live theme arrives.
        // A cached theme is only a network-failure fallback; rendering it here
        // caused a stale logo/palette frame before the admin's latest values.
        setReady(Boolean(!next));
        setTenantSlug(next);
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [applyTheme, tenantSlug]);

  const value = useMemo(() => ({
    theme: theme || {},
    loading,
    tenantSlug,
    refresh: () => fetchTheme(tenantSlug),
    /** Used by the admin live-preview pane to push a draft theme onto :root
     * without persisting. Pass null/empty obj to revert to the saved theme. */
    previewTheme: (draft) => applyTheme(draft || theme || {}),
  }), [theme, loading, tenantSlug, fetchTheme, applyTheme]);

  if (tenantSlug && !ready && !isStaffSurface()) {
    return (
      <TenantThemeContext.Provider value={value}>
        <div
          className="player-app player-lobby-refresh min-h-[100dvh] flex items-center justify-center px-6"
          data-testid="tenant-theme-boot"
        >
          <div className="player-lobby-refresh-content flex flex-col items-center text-center" role="status" aria-live="polite">
            <div className="relative mb-5 grid h-32 w-32 place-items-center">
              <span className="absolute inset-0 rounded-full player-lobby-logo-ring" aria-hidden />
              <span className="absolute inset-3 rounded-full bg-[#001735]/75 blur-md" aria-hidden />
              <div className="player-lobby-refresh-logo relative grid h-28 w-28 place-items-center overflow-hidden rounded-full border border-[#F5D76E]/90 bg-[#001735]/75">
                <img
                  src={installIconHrefForSlug(tenantSlug)}
                  alt="Slots Oceans"
                  className="h-full w-full object-contain"
                  decoding="async"
                />
              </div>
            </div>
            <p className="player-lobby-refresh-brand display text-sm font-black uppercase">Slots Oceans</p>
            <p className="display mt-2 text-2xl font-extrabold text-white">Loading your lobby</p>
            <p className="mt-2 max-w-xs text-sm text-white/75">Syncing your latest experience.</p>
            <span className="player-lobby-refresh-progress mt-6" aria-hidden><span /></span>
          </div>
        </div>
      </TenantThemeContext.Provider>
    );
  }

  return (
    <TenantThemeContext.Provider value={value}>
      {children}
    </TenantThemeContext.Provider>
  );
}

export function useTenantTheme() {
  const ctx = useContext(TenantThemeContext);
  if (!ctx) {
    // Return a safe default so components in the tree can render without
    // the provider (e.g. landing page) — hooks must always be callable.
    return { theme: {}, loading: false, tenantSlug: null, refresh: () => {}, previewTheme: () => {} };
  }
  return ctx;
}

export { themeToCssVars };
